import { FC } from 'react';
import Link from 'next/link';

import { buildSanityImageUrl } from '@lib/sanity/image';
import type { SanityHandoffBlock, SanityQuizTheme } from '@lib/sanity/types';

type HandoffBlockProps = SanityHandoffBlock & {
  theme?: SanityQuizTheme;
};

const HandoffBlock: FC<HandoffBlockProps> = ({
  heading,
  valueProps,
  buttonText,
  buttonUrl
}) => (
  <section className="flex min-h-screen items-center justify-center px-6">
    <div className="w-full max-w-sm text-center">
      {/* Decorative accent */}
      <div className="mx-auto mb-6 h-1 w-10 rounded-full bg-primary-600" />

      <h2
        className="mb-10 font-heading font-black text-neutral-800 leading-tight tracking-tight text-center"
        style={{
          fontSize: 'clamp(1.6rem, 4vw, 2rem)'
        }}
      >
        {heading}
      </h2>

      <div className="mb-10 flex flex-col gap-3">
        {valueProps?.map((prop, index) => {
          const iconUrl = prop.icon
            ? buildSanityImageUrl(prop.icon).width(40).height(40).url()
            : null;

          return (
            <div
              key={prop.title}
              className="flex items-center gap-4 text-left transition-all duration-300 py-[18px] px-5 rounded-md bg-white border border-neutral-300"
              style={{
                animationDelay: `${index * 100}ms`
              }}
            >
              {iconUrl ? (
                <img
                  src={iconUrl}
                  alt=""
                  className="h-9 w-9 flex-shrink-0 rounded"
                />
              ) : (
                <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded font-heading bg-primary-50 text-primary-600 font-bold text-sm">
                  {index + 1}
                </div>
              )}
              <div>
                <p className="font-body text-sm font-bold text-neutral-800 mb-0.5">
                  {prop.title}
                </p>
                <p className="font-body text-xs text-neutral-500">
                  {prop.description}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      <Link
        href={buttonUrl}
        className="inline-block w-full font-body transition-all duration-200 hover:shadow-lg py-4 px-8 rounded text-sm font-semibold tracking-wide bg-primary-600 text-white no-underline text-center"
        style={{
          boxShadow: '0 4px 16px rgba(0, 157, 229, 0.25)'
        }}
      >
        {buttonText}
      </Link>
    </div>
  </section>
);

export default HandoffBlock;
